import { ProductEntity } from '../entities/Product';
import { MovementEntity } from '../entities/Movement';
// import { ProductVariantEntity } from '../entities/ProductVariant';
import type { IProductRepository, IRepository } from '../interfaces/repositories';
import type { IBarcodeService } from './BarcodeService';
import type { Money } from '../shared/types';
import { MovementTypes, MovementStatusTypes } from '../shared/types';
import type { UpdateProductDTO } from '../../application/dtos/product/UpdateProductDTO';

// Import DTO from application layer to avoid duplication
export interface CreateProductDTO {
  name: string;
  sku: string;
  description?: string;
  barcode?: string;
  category?: string;
  cost: Money;
  price: Money;
  minQuantity: { value: number; unit: string };
  maxQuantity?: { value: number; unit: string };
  isActive?: boolean;
  // Initial inventory fields (optional)
  initialQuantity?: number;
  warehouseId?: string;
  locationId?: string;
}

// Movement Repository Interface (minimal for now)
export interface IMovementRepository {
  save(movement: MovementEntity): Promise<void>;
  findByProductId(productId: string): Promise<MovementEntity[]>;
}

// export interface CreateVariantDTO {
//   productId: string;
//   sku: string;
//   barcode?: string;
//   attributes: ProductAttribute[];
//   price: Money;
//   cost: Money;
//   minQuantity: { value: number; unit: string };
//   maxQuantity?: { value: number; unit: string };
//   isActive?: boolean;
// }

export class ProductManagementService {
  constructor(
    private readonly productRepository: IProductRepository,
    // private readonly variantRepository: IRepository<ProductVariant>,
    private readonly barcodeService: IBarcodeService,
    private readonly movementRepository?: IMovementRepository
  ) { }

  async createProduct(data: CreateProductDTO): Promise<ProductEntity> {
    const existingBySku = await this.productRepository.findBySku(data.sku);
    if (existingBySku) {
      throw new Error(`Product with SKU ${data.sku} already exists`);
    }

    if (data.barcode) {
      if (!this.barcodeService.validateBarcode(data.barcode)) {
        throw new Error(`Invalid barcode format: ${data.barcode}`);
      }

      const existingByBarcode = await this.productRepository.findByBarcode(data.barcode);
      if (existingByBarcode) {
        throw new Error(`Product with barcode ${data.barcode} already exists`);
      }
    }

    const product = ProductEntity.create({
      ...data,
      isActive: data.isActive ?? true
    });
    
    try {
      // Save product first
      await this.productRepository.save(product);
      
      // If initial quantity is provided, create initial movement
      if (data.initialQuantity !== undefined && data.initialQuantity > 0 && data.warehouseId) {
        if (!this.movementRepository) {
          throw new Error('Movement repository not configured');
        }
        
        const movement = MovementEntity.create({
          movementType: MovementTypes.ADJUSTMENT,
          productId: product._id, // Use _id from entity
          warehouseId: data.warehouseId,
          quantity: { value: data.initialQuantity, unit: data.minQuantity.unit },
          unitCost: data.cost,
          status: MovementStatusTypes.COMPLETED,
          initiatedBy: 'system', // TODO: Get from auth context
          completedBy: 'system',
          toLocationId: data.locationId,
          reason: 'Initial inventory',
          notes: `Initial inventory for product ${product.name} (SKU: ${product.sku})`
        });
        
        await this.movementRepository.save(movement);
      }
      
      return product;
    } catch (error) {
      // TODO: Implement proper rollback mechanism
      // For now, we'll let the error propagate
      throw error;
    }
  }

  async updateProduct(id: string, dto: UpdateProductDTO): Promise<ProductEntity> {
    const product = await this.productRepository.findById(id);
    if (!product) {
      throw new Error(`Product with ID ${id} not found`);
    }

    if (dto.barcode && dto.barcode !== product.barcode) {
      if (!this.barcodeService.validateBarcode(dto.barcode)) {
        throw new Error(`Invalid barcode format: ${dto.barcode}`);
      }
      const existingByBarcode = await this.productRepository.findByBarcode(dto.barcode);
      if (existingByBarcode) {
        throw new Error(`Product with barcode ${dto.barcode} already exists`);
      }
    }

    if (dto.price) product.updatePrice(dto.price);
    if (dto.cost) product.updateCost(dto.cost);
    if (dto.isActive === false) product.deactivate();

    const props = product.toJSON();
    if (dto.name !== undefined) props.name = dto.name;
    if (dto.sku !== undefined) props.sku = dto.sku;
    if (dto.description !== undefined) props.description = dto.description;
    if (dto.barcode !== undefined) props.barcode = dto.barcode;
    if (dto.category !== undefined) props.category = dto.category;
    if (dto.minQuantity !== undefined) props.minQuantity = dto.minQuantity;
    if (dto.maxQuantity !== undefined) props.maxQuantity = dto.maxQuantity;
    props.updatedAt = new Date();

    const updated = ProductEntity.restore(props);
    await this.productRepository.save(updated);
    return updated;
  }

  // async createVariant(data: CreateVariantDTO): Promise<ProductVariant> {
  //   const product = await this.productRepository.findById(data.productId);
  //   if (!product) {
  //     throw new Error(`Product with ID ${data.productId} not found`);
  //   }

  //   const existingBySku = await this.findVariantBySku(data.sku);
  //   if (existingBySku) {
  //     throw new Error(`Variant with SKU ${data.sku} already exists`);
  //   }

  //   if (data.barcode) {
  //     if (!this.barcodeService.validateBarcode(data.barcode)) {
  //       throw new Error(`Invalid barcode format: ${data.barcode}`);
  //     }

  //     const existingByBarcode = await this.findVariantByBarcode(data.barcode);
  //     if (existingByBarcode) {
  //       throw new Error(`Variant with barcode ${data.barcode} already exists`);
  //     }
  //   }

  //   const variant = ProductVariantEntity.create({
  //     ...data,
  //     isActive: data.isActive ?? true
  //   });
  //   await this.variantRepository.save(variant.toJSON());
  //   return variant.toJSON();
  // }

  // async findVariantsByAttributes(productId: string, attributes: ProductAttribute[]): Promise<ProductVariant[]> {
  //   const variants = await this.variantRepository.findAll();
  //   return variants.filter(v => 
  //     v.productId === productId && 
  //     this.matchesAttributes(v.attributes, attributes)
  //   );
  // }

  // private matchesAttributes(variantAttrs: ProductAttribute[], searchAttrs: ProductAttribute[]): boolean {
  //   return searchAttrs.every(searchAttr =>
  //     variantAttrs.some(varAttr =>
  //       varAttr.name === searchAttr.name && varAttr.value === searchAttr.value
  //     )
  //   );
  // }

  // private async findVariantBySku(sku: string): Promise<ProductVariant | null> {
  //   const variants = await this.variantRepository.findAll();
  //   return variants.find(v => v.sku === sku) ?? null;
  // }

  // private async findVariantByBarcode(barcode: string): Promise<ProductVariant | null> {
  //   const variants = await this.variantRepository.findAll();
  //   return variants.find(v => v.barcode === barcode) ?? null;
  // }
}